import type { LayoutElement } from '../types';
import {
  ALL_RAIL_TYPES,
  ENDPOINT_TOLERANCE,
  getRailEndpoints,
  isCurveRailType,
  isJunctionRailType,
  resolveRailType,
  type Pt,
} from './topology';

export type TrackNodeRef = { nodeId: string; x: number; y: number };

export type TrackNodeMoveCheck = {
  ok: boolean;
  reason?: string;
  attached: string[];
};

export type TrackNodeMovePlan = {
  updates: Array<{ id: string; updates: Partial<LayoutElement> }>;
};

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

type StraightEndpointHit = {
  el: LayoutElement;
  tip: Pt;
};

function straightEndpointsAtPoint(
  elements: readonly LayoutElement[],
  point: Pt
): StraightEndpointHit[] {
  const hits: StraightEndpointHit[] = [];
  for (const el of elements) {
    if (!ALL_RAIL_TYPES.has(el.type) || !el.metadata.visible) continue;
    const railType = resolveRailType(el);
    if (railType !== 'straight') continue;
    const ends = getRailEndpoints(el);
    if (ends.length < 2) continue;
    const [a, b] = ends;
    if (dist(a, point) <= ENDPOINT_TOLERANCE) hits.push({ el, tip: a });
    else if (dist(b, point) <= ENDPOINT_TOLERANCE) hits.push({ el, tip: b });
  }
  return hits;
}

function hasBlockingRailAtPoint(elements: readonly LayoutElement[], point: Pt): boolean {
  for (const el of elements) {
    if (!ALL_RAIL_TYPES.has(el.type) || !el.metadata.visible) continue;
    const railType = resolveRailType(el);
    if (!isCurveRailType(railType) && !isJunctionRailType(railType)) continue;
    for (const tip of getRailEndpoints(el)) {
      if (dist(tip, point) <= ENDPOINT_TOLERANCE) return true;
    }
  }
  return false;
}

function translateRailTipTo(moved: LayoutElement, fromTip: Pt, toTip: Pt): { x: number; y: number } {
  return {
    x: moved.position.x + (toTip.x - fromTip.x),
    y: moved.position.y + (toTip.y - fromTip.y),
  };
}

/**
 * Whether a simulation graph node can drag-move every straight-track end that meets at its position.
 */
export function checkTrackNodeMove(
  elements: readonly LayoutElement[],
  node: TrackNodeRef
): TrackNodeMoveCheck {
  const point = { x: node.x, y: node.y };

  if (hasBlockingRailAtPoint(elements, point)) {
    return {
      ok: false,
      reason: 'Curve and junction nodes stay fixed — drag straight track ends only',
      attached: [],
    };
  }

  const hits = straightEndpointsAtPoint(elements, point);
  if (hits.length === 0) {
    return {
      ok: false,
      reason: 'No straight track ends meet at this node',
      attached: [],
    };
  }

  if (hits.some((h) => h.el.metadata.locked)) {
    return {
      ok: false,
      reason: 'A connected track is locked',
      attached: [],
    };
  }

  return {
    ok: true,
    attached: hits.map((h) => h.el.id),
  };
}

/** Move every straight endpoint at the node to `target` (shared corner drag). */
export function planTrackNodeMove(
  elements: readonly LayoutElement[],
  node: TrackNodeRef,
  target: Pt
): TrackNodeMovePlan {
  const check = checkTrackNodeMove(elements, node);
  if (!check.ok) return { updates: [] };

  const point = { x: node.x, y: node.y };
  if (dist(point, target) <= 1e-6) return { updates: [] };

  const hits = straightEndpointsAtPoint(elements, point);
  const updates: TrackNodeMovePlan['updates'] = [];
  const seen = new Set<string>();

  for (const { el, tip } of hits) {
    if (seen.has(el.id)) continue;
    seen.add(el.id);
    const pos = translateRailTipTo(el, tip, target);
    updates.push({
      id: el.id,
      updates: {
        position: {
          ...el.position,
          x: pos.x,
          y: pos.y,
        },
      },
    });
  }

  return { updates };
}
